import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { overlayDismiss } from '../utils/modalDismiss';
import { toDateTimeLocal } from '../utils/attendance';

const MODES = [
  ['named', 'Named', 'Name and email required; one response per person (sending again replaces it)'],
  ['optional', 'Optional', 'Trainees may leave name and email blank'],
  ['anonymous', 'Anonymous', 'Name and email are not asked']
];

const lines = (text) => text.split('\n').map(s => s.trim()).filter(Boolean);

// Site/Location or Function: free text, or a dropdown whose options can be typed
// or copied from an exam session's "Extra details" question.
const OptionsField = ({ label, value, onChange, sessions }) => {
  const [copySession, setCopySession] = useState('');
  const [fields, setFields] = useState([]);

  useEffect(() => {
    setFields([]);
    if (!copySession) return;
    api.get(`/sessions/${copySession}/intro-fields`)
      .then(r => setFields(r.data.filter(f => f.field_type === 'dropdown' && (f.options || []).length)))
      .catch(() => setFields([]));
  }, [copySession]);

  return (
    <div className="form-group">
      <label>{label}</label>
      <div className="flex gap-2 mb-2">
        {[[false, 'Free text'], [true, 'Dropdown list']].map(([isList, text]) => (
          <button key={text} type="button"
            className={`btn btn-sm ${value.isList === isList ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => onChange({ ...value, isList })}>{text}</button>
        ))}
      </div>
      {value.isList && (
        <>
          <textarea rows={4} placeholder="One option per line" value={value.text}
            onChange={e => onChange({ ...value, text: e.target.value })} />
          <div className="flex gap-2 mt-2" style={{ flexWrap: 'wrap', alignItems: 'center' }}>
            <span className="text-muted text-sm">Copy from exam session:</span>
            <select style={{ flex: '1 1 200px' }} value={copySession} onChange={e => setCopySession(e.target.value)}>
              <option value="">— Choose session —</option>
              {sessions.map(s => <option key={s.id} value={s.id}>{s.session_name}</option>)}
            </select>
            {copySession && (
              fields.length === 0
                ? <span className="text-muted text-sm">No dropdown questions in that session</span>
                : fields.map(f => (
                    <button key={f.id} type="button" className="btn btn-secondary btn-sm"
                      onClick={() => onChange({ isList: true, text: f.options.join('\n') })}>
                      Use "{f.label}" ({f.options.length})
                    </button>
                  ))
            )}
          </div>
          <p className="text-muted text-sm mt-1">Trainees can also choose "Other" and type their own.</p>
        </>
      )}
    </div>
  );
};

/**
 * Create or edit a feedback form. `form` is the existing form when editing;
 * `trainingId` presets the training when creating from a training page.
 */
const FeedbackSettingsModal = ({ form, trainingId, onClose, onSaved }) => {
  const editing = !!form;
  const locked = editing && form.response_count > 0;
  const [trainings, setTrainings] = useState([]);
  const [categories, setCategories] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [v, setV] = useState(() => ({
    title: form?.title || '',
    template: form?.template || 'B',
    identity_mode: form?.identity_mode || 'named',
    training_id: form?.training_id || trainingId || '',
    category_id: form?.category_id || '',
    trainers: (form?.trainer_names || []).join('\n'),
    site: { isList: !!form?.site_options, text: (form?.site_options || []).join('\n') },
    func: { isList: !!form?.function_options, text: (form?.function_options || []).join('\n') },
    starts_at: form?.starts_at ? toDateTimeLocal(form.starts_at) : '',
    ends_at: form?.ends_at ? toDateTimeLocal(form.ends_at) : ''
  }));
  const set = (patch) => setV(prev => ({ ...prev, ...patch }));

  useEffect(() => {
    api.get('/attendance/trainings').then(r => setTrainings(r.data)).catch(() => {});
    api.get('/categories').then(r => setCategories(r.data)).catch(() => {});
    api.get('/sessions').then(r => setSessions(r.data)).catch(() => {});
  }, []);

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      title: v.title,
      template: v.template,
      identity_mode: v.identity_mode,
      trainer_names: lines(v.trainers),
      site_options: v.site.isList ? lines(v.site.text) : null,
      function_options: v.func.isList ? lines(v.func.text) : null,
      starts_at: v.starts_at ? new Date(v.starts_at).toISOString() : null,
      ends_at: v.ends_at ? new Date(v.ends_at).toISOString() : null
    };
    if (!editing) {
      payload.training_id = v.training_id || null;
      payload.category_id = v.training_id ? null : (v.category_id || null);
    }
    setSaving(true);
    try {
      const { data } = editing
        ? await api.put(`/feedback/${form.id}`, payload)
        : await api.post('/feedback', payload);
      onSaved(data);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to save');
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" {...overlayDismiss(onClose)}>
      <div className="modal" style={{ maxWidth: '640px' }}>
        <div className="modal-header">
          <h3>{editing ? 'Edit Feedback Form' : 'New Feedback Form'}</h3>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <form onSubmit={save}>
          <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
            <div className="form-group">
              <label>Title</label>
              <input type="text" required maxLength={255} autoFocus value={v.title}
                placeholder="e.g. Certified Pharma Investigator - Oct 2026 batch feedback"
                onChange={e => set({ title: e.target.value })} />
            </div>

            {!editing && (
              <div className="form-group">
                <label>Training (optional)</label>
                <select value={v.training_id} onChange={e => set({ training_id: e.target.value })}>
                  <option value="">— None (standalone form) —</option>
                  {trainings.map(t => <option key={t.id} value={t.id}>{t.title}</option>)}
                </select>
              </div>
            )}
            {!editing && !v.training_id && (
              <div className="form-group">
                <label>Category (optional)</label>
                <select value={v.category_id} onChange={e => set({ category_id: e.target.value })}>
                  <option value="">— None —</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            )}

            <div className="form-group">
              <label>Questions</label>
              {[['A', 'Standard', 'The Microsoft Form questions (16 ratings, overall rating, 2 comment questions), optionally a rating per trainer'],
                ['B', 'Improved', '22 ratings incl. pace, exercises and learning, one rating per trainer, 3 comment questions']]
                .map(([key, name, desc]) => (
                  <label key={key} className="flex gap-2 mb-2" style={{ cursor: locked ? 'default' : 'pointer', alignItems: 'flex-start' }}>
                    <input type="radio" name="template" checked={v.template === key} disabled={locked}
                      onChange={() => set({ template: key })} style={{ marginTop: '4px' }} />
                    <span><strong>{name}</strong> <span className="text-muted text-sm">— {desc}</span></span>
                  </label>
                ))}
            </div>

            <div className="form-group">
              <label>Names</label>
              {MODES.map(([key, name, desc]) => (
                <label key={key} className="flex gap-2 mb-2" style={{ cursor: locked ? 'default' : 'pointer', alignItems: 'flex-start' }}>
                  <input type="radio" name="mode" checked={v.identity_mode === key} disabled={locked}
                    onChange={() => set({ identity_mode: key })} style={{ marginTop: '4px' }} />
                  <span><strong>{name}</strong> <span className="text-muted text-sm">— {desc}</span></span>
                </label>
              ))}
              {locked && <p className="text-muted text-sm">Questions and names can't change once responses have been received.</p>}
            </div>

            <div className="form-group">
              <label>Trainer names (one per line{v.template === 'A' ? ', optional' : ''})</label>
              <textarea rows={3} value={v.trainers} placeholder={'Dr A. Rao\nMs B. Iyer'}
                onChange={e => set({ trainers: e.target.value })} />
              <p className="text-muted text-sm mt-1">
                Trainees give each trainer an overall rating, 1 (Poor) to 5 (Excellent) or N/A.
                {v.template === 'A' && ' Leave empty for no trainer ratings.'}
              </p>
            </div>

            <OptionsField label="Site Name/Location" value={v.site} onChange={site => set({ site })} sessions={sessions} />
            <OptionsField label="Function" value={v.func} onChange={func => set({ func })} sessions={sessions} />

            <div className="grid grid-2">
              <div className="form-group">
                <label>Opens (optional)</label>
                <input type="datetime-local" value={v.starts_at} onChange={e => set({ starts_at: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Closes (optional)</label>
                <input type="datetime-local" value={v.ends_at} onChange={e => set({ ends_at: e.target.value })} />
              </div>
            </div>
            <p className="text-muted text-sm">Leave both empty to keep the form open; set a closing time later to stop responses.</p>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving...' : editing ? 'Save' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FeedbackSettingsModal;
